import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import {
  MessageSquare, Activity, StickyNote, Stethoscope,
  Search, Pill, FileText, Calendar, FileEdit,
} from "lucide-react";

import { usePrescriptionForm } from "@/hooks/usePrescriptionForm";
import { usePrescriptionSections } from "@/hooks/usePrescriptionSections";
import { usePrescriptionLayout } from "@/hooks/usePrescriptionLayout";
import FormSectionWrapper from "@/components/PrescriptionForm/FormSectionWrapper";
import FormActionBar from "@/components/PrescriptionForm/StickyBar";
import { PrescriptionFormData } from "@/types/prescription";
import { Patient } from "@/types";
import { Doctor } from "@/types/doctor";

import ComplaintsGrid from "@/components/PrescriptionFormSections/ComplaintsGrid";
import VitalsForm from "@/components/PrescriptionFormSections/VitalsForm";
import QuickNotesSection from "@/components/PrescriptionFormSections/QuickNotesSection";
import DiagnosisSection from "@/components/PrescriptionFormSections/DiagnosisSection";
import InvestigationsGrid from "@/components/PrescriptionFormSections/InvestigationsGrid";
import MedicineListForm from "@/components/PrescriptionFormSections/MedicineListForm";
import AdviceSection from "@/components/PrescriptionFormSections/AdviceSection";
import NextVisitSection from "@/components/PrescriptionFormSections/NextVisitSection";
import CustomFieldSection from "@/components/PrescriptionFormSections/CustomFieldSection";
import AttachmentsSection from "@/components/PrescriptionFormSections/AttachmentsSection";
import { StagedAttachment } from "@/components/PrescriptionFormSections/PendingAttachments";

import LoadTemplateModal from "@/components/Template/LoadTemplateModal";
import SaveTemplateModal from "@/components/Template/SaveTemplateModal";
import { uploadPatientDocuments } from "@/lib/patientDocumentsApi";
import { toast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormSection {
  id: string;
  icon: React.ReactNode;
  label: string;
  content: React.ReactNode;
  hasTemplate?: boolean;
  hasClear?: boolean;
  required?: boolean;
}

interface PrescriptionFormProps {
  mode?: "create" | "edit";
  patientId: string;
  initialData?: Partial<PrescriptionFormData>;
  onSaved?: () => void;
  onCancel?: () => void;
  showStickyBar?: boolean;
  onFormReady?: (submitFn: () => void, hasChanges: boolean, isSubmitting: boolean, hasValidChanges: boolean, attachmentsSlot: React.ReactNode, pendingAttachmentsCount: number) => void;
  patient?: Patient;
  doctor?: Doctor;
}

// Sections a doctor may opt to hide via Settings → Prescription Layout.
// Complaints, Diagnosis, and Medications are always shown.
const HIDEABLE_SECTIONS = new Set(["vitals", "investigations", "advice", "notes", "next-visit"]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dobToAgeMonths(dob: string | undefined): number | undefined {
  if (!dob) return undefined;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return undefined;
  const now = new Date();
  const months =
    (now.getFullYear() - birth.getFullYear()) * 12 +
    (now.getMonth() - birth.getMonth());
  return now.getDate() >= birth.getDate() ? months : months - 1;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PrescriptionForm({
                                           mode = "create",
                                           patientId,
                                           initialData,
                                           onSaved,
                                           onCancel,
                                           showStickyBar = true,
                                           onFormReady,
                                           patient,
                                           doctor,
                                         }: PrescriptionFormProps) {

  const patientAgeMonths = dobToAgeMonths(patient?.date_of_birth);
  const specialty        = doctor?.specialty ?? "General Medicine";

  // ── Attachments staged before the prescription has an id ───────────────────
  // (see AttachmentsSection / PendingAttachments). Flushed once save succeeds
  // and a real prescription_id exists to link them to.

  const pendingAttachmentsRef = useRef<StagedAttachment[]>([]);
  // Reactive twin of the ref above — the ref alone can't drive onFormReady
  // (which needs to notify the parent when the count actually changes so it
  // can warn on Cancel; a plain ref update doesn't trigger anything).
  const [pendingAttachmentsCount, setPendingAttachmentsCount] = useState(0);

  const handleSaved = useCallback(async (prescriptionId?: string) => {
    const pending = pendingAttachmentsRef.current;
    pendingAttachmentsRef.current = [];
    setPendingAttachmentsCount(0);

    if (prescriptionId && pending.length > 0) {
      let createdCount = 0;
      let failedCount = 0;
      for (const item of pending) {
        try {
          const result = await uploadPatientDocuments(patientId, { prescriptionId, ...item.meta, files: item.files });
          createdCount += result.created.length;
          failedCount += result.errors.length;
        } catch {
          failedCount += item.files.length;
        }
      }
      if (createdCount > 0) {
        toast({ title: `Attached ${createdCount} document${createdCount > 1 ? "s" : ""}` });
      }
      if (failedCount > 0) {
        toast({
          title: `${failedCount} attachment${failedCount > 1 ? "s" : ""} failed to upload`,
          description: "The prescription itself was saved — you can retry attaching from the prescriptions list.",
          variant: "destructive",
        });
      }
    }

    onSaved?.();
  }, [patientId, onSaved]);

  // ── Form hooks ──────────────────────────────────────────────────────────────

  const formHook     = usePrescriptionForm({ mode, patientId, initialData, onSaved: handleSaved, onCancel });
  const sectionsHook = usePrescriptionSections();
  const { layout: prescriptionLayout } = usePrescriptionLayout();

  // Rendered once, placed next to Save — either in PrescriptionForm's own
  // FormActionBar (showStickyBar=true) or handed to the parent page via
  // onFormReady to render in its own externally-owned action bar
  // (showStickyBar=false, e.g. PatientDetailPage's create-prescription flow).
  // Memoized so its reference (and therefore AttachmentsSection's own mounted
  // state — dialog open/closed, staged count) stays stable across the
  // frequent re-renders this form gets from watch()-driven field updates.
  const attachmentsElement = useMemo(() => (
    <AttachmentsSection
      patientId={patientId}
      prescriptionId={mode === "edit" ? initialData?.id : undefined}
      onPendingChange={(items) => {
        pendingAttachmentsRef.current = items;
        setPendingAttachmentsCount(items.length);
      }}
    />
  ), [patientId, mode, initialData?.id]);

  // Stable ref for onFormReady — avoids re-running the effect when parent
  // passes a new inline function reference on every render
  const onFormReadyRef = useRef(onFormReady);
  useEffect(() => { onFormReadyRef.current = onFormReady; }, [onFormReady]);

  useEffect(() => {
    onFormReadyRef.current?.(formHook.submitForm, formHook.hasChanges, formHook.isSubmitting, Boolean(formHook.hasValidChanges), attachmentsElement, pendingAttachmentsCount);
  }, [formHook.submitForm, formHook.hasChanges, formHook.isSubmitting, formHook.hasValidChanges, attachmentsElement, pendingAttachmentsCount]);

  // ── Section clearing ────────────────────────────────────────────────────────

  const handleClearSection = useCallback((sectionId: string, sectionLabel: string) => {
    const result = sectionsHook.handleClearSection(sectionId, sectionLabel);
    if (!result) return;
    const { setValue } = formHook.form;
    switch (sectionId) {
      case "complaints":     setValue("complaints",     []); break;
      case "vitals":         setValue("vitals",         {}); break;
      case "diagnosis":      setValue("diagnosis",      ""); break;
      case "medicines":      setValue("medicines",      []); break;
      case "investigations": setValue("investigations", []); break;
      case "advice":         setValue("advice",         ""); break;
      case "notes":          setValue("quick_notes",    ""); break;
      case "next-visit":     setValue("next_visit",     {}); break;
      default:
        if (sectionId.startsWith("custom_")) {
          setValue(`custom_fields.${sectionId}` as any, "");
        }
        break;
    }
  }, [sectionsHook, formHook.form]);

  // ── Template apply ──────────────────────────────────────────────────────────

  const handleApplyTemplate = useCallback((template: any, mode: "replace" | "append") => {
    const type = sectionsHook.activeLoadType;
    if (!type || !template.value) return;
    const { setValue, getValues } = formHook.form;

    // Always parse value — API may return a JSON string
    const raw = typeof template.value === "string"
        ? (() => { try { return JSON.parse(template.value); } catch { return template.value; } })()
        : template.value;

    const dirty = { shouldDirty: true, shouldTouch: true };

    switch (type) {
      case "complaint": {
        if (!Array.isArray(raw)) break;
        const incoming = raw.map((item: any) =>
            typeof item === "string"
                ? { complaint: item, frequency: "", severity: "", duration: "", date: "" }
                : { complaint: item.complaint || "", frequency: item.frequency || "",
                  severity: item.severity || "", duration: item.duration || "", date: item.date || "" }
        );
        const next = mode === "append"
            ? [
              ...(getValues("complaints") || []).filter((c: any) => c.complaint?.trim()),
              ...incoming,
            ]
            : incoming;
        setValue("complaints", next, dirty);
        break;
      }

      case "medicine": {
        if (!Array.isArray(raw)) break;
        const incoming = raw.map((m: any) => ({
          drug_id:      null,
          product:      m.brand_name  || m.product || "",
          brand_name:   m.brand_name  || m.product || "",
          generic_name: m.generic_name || "",
          dosage:       m.dosage       ?? "",
          taken_when:   m.taken_when   ?? m.when ?? "",
          duration:     m.duration     ?? "",
          quantity:     m.quantity     ?? "",
          notes:        m.notes        ?? "",
          _drug_form:   "",
          _drug_brand:  "",
        }));
        const next = mode === "append"
            ? [
              ...(getValues("medicines") || []).filter((m: any) => m.product?.trim() || m.brand_name?.trim()),
              ...incoming,
            ]
            : incoming;
        setValue("medicines", next, dirty);
        break;
      }

      case "investigation": {
        if (!Array.isArray(raw)) break;
        const next = mode === "append"
            ? [
              ...(getValues("investigations") || []).filter((i: any) => typeof i === "string" ? i.trim() : i),
              ...raw,
            ]
            : raw;
        setValue("investigations", next, dirty);
        break;
      }

      case "diagnosis": {
        const text = typeof raw?.value === "string" ? raw.value : typeof raw === "string" ? raw : null;
        if (text !== null) setValue("diagnosis", text, dirty);
        break;
      }

      case "advice": {
        const text = typeof raw?.value === "string" ? raw.value : typeof raw === "string" ? raw : null;
        if (text !== null) setValue("advice", text, dirty);
        break;
      }

      case "quick_notes": {
        const text = typeof raw?.value === "string" ? raw.value : typeof raw === "string" ? raw : null;
        if (text !== null) setValue("quick_notes", text, dirty);
        break;
      }
    }

    sectionsHook.closeLoadTemplate();
  }, [sectionsHook, formHook.form]);

  // ── Template save ───────────────────────────────────────────────────────────

  const getTemplateData = useCallback((type: string) => {
    const values = formHook.form.getValues();
    switch (type) {
      case "complaint":     return values.complaints;
      case "medicine":      return values.medicines;
      case "investigation": return values.investigations;
      case "diagnosis":     return values.diagnosis;
      case "advice":        return values.advice;
      case "quick_notes":   return values.quick_notes;
      default:              return null;
    }
  }, [formHook.form]);

  const getSuggestedTitle = useCallback((type: string): string => {
    const values = formHook.form.getValues();
    try {
      switch (type) {
        case "complaint": {
          const first = values.complaints?.[0]?.complaint;
          return first ? `${first} template` : "";
        }
        case "medicine": {
          const first = values.medicines?.[0]?.brand_name || values.medicines?.[0]?.product;
          return first ? `${first} regimen` : "";
        }
        case "investigation": {
          const first = values.investigations?.[0];
          return first ? `${first} panel` : "";
        }
        case "diagnosis": {
          const d = values.diagnosis;
          return typeof d === "string" && d.trim() ? d.trim().slice(0, 40) : "";
        }
        default: return "";
      }
    } catch { return ""; }
  }, [formHook.form]);

  // ── Sections config — memoized so JSX content elements are stable ───────────

  // Section order: Complaints → Vitals → Diagnosis → Medications → Investigations → Advice → Notes → Next Visit → Custom Fields
  const sections: FormSection[] = useMemo(() => {
    const hidden = new Set(prescriptionLayout.hiddenSections);

    const baseSections: FormSection[] = [
      {
        id: "complaints", icon: <MessageSquare className="w-3.5 h-3.5" />,
        label: "Complaints", content: <ComplaintsGrid />,
        hasTemplate: true, hasClear: true, required: false,
      },
      {
        id: "vitals", icon: <Activity className="w-3.5 h-3.5" />,
        label: "Vital Signs",
        content: <VitalsForm patientAgeMonths={patientAgeMonths} />,
        hasClear: true,
      },
      {
        id: "diagnosis", icon: <Stethoscope className="w-3.5 h-3.5" />,
        label: "Diagnosis",
        content: <DiagnosisSection specialty={specialty} />,
        hasClear: true, required: false,
      },
      {
        id: "medicines", icon: <Pill className="w-3.5 h-3.5" />,
        label: "Medications",
        content: <MedicineListForm patient={patient} />,
        hasTemplate: true, hasClear: true,
      },
      {
        id: "investigations", icon: <Search className="w-3.5 h-3.5" />,
        label: "Investigations",
        content: <InvestigationsGrid specialty={specialty} />,
        hasTemplate: true, hasClear: true,
      },
      {
        id: "advice", icon: <FileText className="w-3.5 h-3.5" />,
        label: "Advice", content: <AdviceSection />,
        hasTemplate: true, hasClear: true,
      },
      {
        id: "notes", icon: <StickyNote className="w-3.5 h-3.5" />,
        label: "Quick Notes", content: <QuickNotesSection />,
        hasClear: true,
      },
      {
        id: "next-visit", icon: <Calendar className="w-3.5 h-3.5" />,
        label: "Next Visit", content: <NextVisitSection />,
        hasClear: true,
      },
    ].filter((section) => !HIDEABLE_SECTIONS.has(section.id) || !hidden.has(section.id));

    const customSections: FormSection[] = prescriptionLayout.customFields
      .filter((cf) => cf.enabled && cf.label.trim())
      .map((cf) => ({
        id: cf.key,
        icon: <FileEdit className="w-3.5 h-3.5" />,
        label: cf.label,
        content: <CustomFieldSection fieldKey={cf.key} />,
        hasClear: true,
      }));

    return [...baseSections, ...customSections];
  }, [patientAgeMonths, specialty, patient, prescriptionLayout]);

  const getSectionErrors = useCallback((sectionId: string): boolean => {
    switch (sectionId) {
      case "complaints":     return !!formHook.errors.complaints;
      case "vitals":         return !!formHook.errors.vitals;
      case "diagnosis":      return !!formHook.errors.diagnosis;
      case "medicines":      return !!formHook.errors.medicines;
      case "investigations": return !!formHook.errors.investigations;
      case "advice":         return !!formHook.errors.advice;
      case "notes":          return !!formHook.errors.quick_notes;
      case "next-visit":     return !!formHook.errors.next_visit;
      default:               return false;
    }
  }, [formHook.errors]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
      <FormProvider {...formHook.form}>
        <div className="space-y-2">

          {sections.map((section) => (
              <FormSectionWrapper
                  key={section.id}
                  section={section}
                  isMinimized={sectionsHook.minimizedSections[section.id] || false}
                  hasErrors={getSectionErrors(section.id)}
                  sectionCount={sectionsHook.getSectionCount(section.id, formHook.watchedValues)}
                  onMinimize={sectionsHook.handleMinimizeSection}
                  onExpand={sectionsHook.handleExpandSection}
                  onLoadTemplate={sectionsHook.handleLoadTemplate}
                  onSaveTemplate={sectionsHook.handleSaveTemplate}
                  onClearSection={handleClearSection}
              />
          ))}

          {showStickyBar && (
              <FormActionBar
                  hasChanges={formHook.hasChanges}
                  hasValidChanges={Boolean(formHook.hasValidChanges)}
                  isSubmitting={formHook.isSubmitting}
                  mode={formHook.mode}
                  onSubmit={formHook.submitForm}
                  onCancel={formHook.handleCancel}
                  attachmentsSlot={attachmentsElement}
              />
          )}

          {sectionsHook.activeLoadType && (
              <LoadTemplateModal
                  open={true}
                  type={sectionsHook.activeLoadType}
                  onClose={sectionsHook.closeLoadTemplate}
                  onApply={handleApplyTemplate}
              />
          )}

          {sectionsHook.activeSaveType && (
              <SaveTemplateModal
                  open={true}
                  type={sectionsHook.activeSaveType}
                  onClose={sectionsHook.closeSaveTemplate}
                  suggestedTitle={getSuggestedTitle(sectionsHook.activeSaveType)}
                  onSave={async (templateData) => {
                    const activeType = sectionsHook.activeSaveType as
                        | "complaint" | "medicine" | "investigation"
                        | "advice"    | "diagnosis" | "quick_notes";
                    const currentData = getTemplateData(activeType);
                    const finalValue  = ["advice", "diagnosis", "quick_notes"].includes(activeType)
                        ? { value: String(currentData ?? "").trim() }
                        : currentData;
                    await sectionsHook.saveTemplate({ ...templateData, type: activeType, value: finalValue });
                  }}
              />
          )}

        </div>
      </FormProvider>
  );
}